import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, Users, MessageSquare, Bell, UserPlus,
    BookOpen, Palette, TreePine, Heart, ChevronRight, Pin, Check, X, Plus, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CommunityPage() {
    const navigate = useNavigate();
    const { organization } = useOrganization();
    const { token, user } = useAuth();
    const [activeTab, setActiveTab] = useState('noticeboard');
    const [loading, setLoading] = useState(true);
    
    // Data states
    const [announcements, setAnnouncements] = useState([]);
    const [clubs, setClubs] = useState([]);
    
    // Submit notice modal
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [newNotice, setNewNotice] = useState({ title: '', content: '' });

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Fallback data for initial display
    const fallbackAnnouncements = [
        {
            id: '1',
            title: 'Fire Alarm Test - Friday 3pm',
            content: 'Routine fire alarm test will take place this Friday at 3pm. No action required.',
            publish_date: new Date().toISOString(),
            is_pinned: true,
            author_name: 'Management',
            author_role: 'Management'
        },
        {
            id: '2',
            title: 'New Yoga Instructor Joining',
            content: 'We are pleased to welcome Sarah to our wellness team. She will be leading yoga classes starting next week.',
            publish_date: new Date(Date.now() - 86400000).toISOString(),
            is_pinned: false,
            author_name: 'Wellness Team',
            author_role: 'Staff'
        },
    ];

    const fallbackClubs = [
        { id: '1', name: 'Book Club', category: 'reading', meeting_schedule: 'Thursday, 2pm', member_count: 12, is_member: false, icon: BookOpen },
        { id: '2', name: 'Art & Crafts', category: 'crafts', meeting_schedule: 'Tuesday, 10am', member_count: 8, is_member: false, icon: Palette },
        { id: '3', name: 'Walking Group', category: 'fitness', meeting_schedule: 'Daily, 9am', member_count: 15, is_member: false, icon: TreePine },
        { id: '4', name: 'Bridge & Cards', category: 'social', meeting_schedule: 'Wednesday, 3pm', member_count: 16, is_member: false, icon: Heart },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [announcementsRes, clubsRes] = await Promise.all([
                axios.get(`${API}/community/announcements`, { headers }),
                axios.get(`${API}/community/clubs`, { headers })
            ]);
            setAnnouncements(announcementsRes.data.length > 0 ? announcementsRes.data : fallbackAnnouncements);
            setClubs(clubsRes.data.length > 0 ? clubsRes.data : fallbackClubs);
        } catch (error) {
            console.error('Error fetching community data:', error);
            // Use fallback data
            setAnnouncements(fallbackAnnouncements);
            setClubs(fallbackClubs);
        } finally {
            setLoading(false);
        }
    };

    const handleJoinClub = async (clubId) => {
        try {
            await axios.post(`${API}/community/clubs/${clubId}/join`, {}, { headers });
            toast.success('Joined club successfully!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to join club');
        }
    };

    const handleLeaveClub = async (clubId) => {
        try {
            await axios.delete(`${API}/community/clubs/${clubId}/leave`, { headers });
            toast.success('Left club');
            fetchData();
        } catch (error) {
            toast.error('Failed to leave club');
        }
    };

    const handleSubmitNotice = async () => {
        if (!newNotice.title.trim() || !newNotice.content.trim()) {
            toast.error('Please fill in both title and content');
            return;
        }
        
        setSubmitting(true);
        try {
            await axios.post(`${API}/community/notices`, {
                title: newNotice.title.trim(),
                content: newNotice.content.trim(),
            }, { headers });
            
            toast.success('Notice submitted for approval! It will appear once approved by a manager.');
            setShowSubmitModal(false);
            setNewNotice({ title: '', content: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit notice');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffHours < 1) return 'Just now';
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const getClubIcon = (category) => {
        const icons = {
            reading: BookOpen,
            crafts: Palette,
            fitness: TreePine,
            social: Heart,
        };
        return icons[category] || Users;
    };

    return (
        <div className="min-h-screen bg-stone-50 pb-24" data-testid="community-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-purple-700 to-purple-800 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-purple-200 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Home
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Users className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">Community</h1>
                        <p className="text-white/80 text-sm">Clubs, news & neighbours</p>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="px-4 -mt-4 relative z-10">
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-1 flex">
                    {['noticeboard', 'clubs', 'messages'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                                activeTab === tab
                                    ? 'bg-purple-600 text-white'
                                    : 'text-stone-600 hover:bg-stone-100'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="px-4 mt-6">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Noticeboard Tab */}
                        {activeTab === 'noticeboard' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-semibold text-stone-800">Community Noticeboard</h2>
                                    <Button 
                                        onClick={() => setShowSubmitModal(true)}
                                        size="sm"
                                        className="bg-purple-600 hover:bg-purple-700"
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Post Notice
                                    </Button>
                                </div>
                                
                                {/* Info about notice submissions */}
                                <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-4">
                                    <p className="text-sm text-white">
                                        <strong>Share with your community!</strong> Post notices about events, items for sale, 
                                        recommendations, or anything helpful. All notices are reviewed by management before publishing.
                                    </p>
                                </div>
                                
                                {announcements.length === 0 ? (
                                    <div className="bg-stone-100 rounded-xl p-8 text-center">
                                        <Bell className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                        <p className="text-stone-500">No announcements yet</p>
                                        <p className="text-stone-400 text-sm mt-2">Be the first to post a notice!</p>
                                    </div>
                                ) : (
                                    announcements.map((item) => (
                                        <div 
                                            key={item.id} 
                                            className={`bg-white rounded-2xl p-5 border ${
                                                item.is_pinned ? 'border-purple-200 bg-purple-50/50' : 'border-stone-200'
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                {item.is_pinned && (
                                                    <Pin className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
                                                )}
                                                <div className="flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <h3 className="font-semibold text-stone-800">{item.title}</h3>
                                                        {item.community_source && (
                                                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                                                                item.community_source.includes('Elysian') 
                                                                    ? 'bg-amber-100 text-amber-700' 
                                                                    : 'bg-green-100 text-green-700'
                                                            }`}>
                                                                {item.community_source.includes('Elysian') ? 'Elysian' : 'Burston'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-stone-600 text-sm mt-1">{item.content}</p>
                                                    <div className="flex items-center gap-3 mt-3 text-xs text-stone-400">
                                                        <span>{item.author_name}</span>
                                                        <span>•</span>
                                                        <span>{formatDate(item.publish_date)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Clubs Tab */}
                        {activeTab === 'clubs' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-semibold text-stone-800">Resident Clubs</h2>
                                    <Button variant="outline" size="sm">
                                        <UserPlus className="w-4 h-4 mr-1" />
                                        Suggest Club
                                    </Button>
                                </div>
                                
                                {clubs.map((club) => {
                                    const IconComponent = club.icon || getClubIcon(club.category);
                                    return (
                                        <div key={club.id} className="bg-white rounded-2xl p-4 border border-stone-200">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                                                    <IconComponent className="w-6 h-6 text-purple-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold text-stone-800">{club.name}</h3>
                                                        {club.community_source && (
                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                                club.community_source.includes('Elysian') 
                                                                    ? 'bg-amber-100 text-amber-700' 
                                                                    : 'bg-green-100 text-green-700'
                                                            }`}>
                                                                {club.community_source.includes('Elysian') ? 'Elysian' : 'Burston'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-stone-500">
                                                        {club.member_count} members • {club.meeting_schedule}
                                                    </p>
                                                </div>
                                                {club.is_member ? (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        className="text-green-600 border-green-200"
                                                        onClick={() => handleLeaveClub(club.id)}
                                                    >
                                                        <Check className="w-4 h-4 mr-1" />
                                                        Joined
                                                    </Button>
                                                ) : (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleJoinClub(club.id)}
                                                    >
                                                        Join
                                                    </Button>
                                                )}
                                            </div>
                                            {club.description && (
                                                <p className="text-sm text-stone-500 mt-3 ml-16">{club.description}</p>
                                            )}
                                        </div>
                                    );
                                })}

                                <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-5 text-center">
                                    <h3 className="font-semibold text-white mb-2">Start a New Club</h3>
                                    <p className="text-purple-100 text-sm mb-4">
                                        Have an interest you'd like to share? Start a club and connect with like-minded residents.
                                    </p>
                                    <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Propose a Club
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Messages Tab */}
                        {activeTab === 'messages' && (
                            <div className="space-y-6">
                                <div className="bg-white rounded-2xl p-8 border border-stone-200 text-center">
                                    <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                    <h3 className="font-semibold text-stone-700 mb-2">Community Messaging</h3>
                                    <p className="text-stone-500 text-sm mb-4">
                                        A calm, respectful way to connect with neighbours and staff.
                                    </p>
                                    <p className="text-xs text-stone-400">Coming soon</p>
                                </div>

                                {/* AI Assistant Help */}
                                <div 
                                    onClick={() => navigate('/assistant')}
                                    className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-5 cursor-pointer hover:from-purple-700 hover:to-purple-800 transition-all"
                                    data-testid="ai-assistant-help"
                                >
                                    <h3 className="font-semibold text-white mb-2">Need more assistance?</h3>
                                    <p className="text-purple-100 text-sm mb-3">
                                        Our AI assistant can help with community queries.
                                    </p>
                                    <span className="text-white font-bold flex items-center gap-2">
                                        Chat with Assistant
                                        <ChevronRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Submit Notice Modal */}
            <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Post a Community Notice</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label htmlFor="notice-title">Title</Label>
                            <Input 
                                id="notice-title"
                                value={newNotice.title}
                                onChange={(e) => setNewNotice(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g., Book swap this Saturday!"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="notice-content">Notice Content</Label>
                            <Textarea 
                                id="notice-content"
                                value={newNotice.content}
                                onChange={(e) => setNewNotice(prev => ({ ...prev, content: e.target.value }))}
                                placeholder="Share details about your notice, event, or announcement..."
                                rows={4}
                                className="mt-1"
                            />
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                            <p className="text-xs text-amber-700">
                                <strong>Note:</strong> Your notice will be reviewed by management before appearing on the noticeboard. This usually takes 1-2 hours.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setShowSubmitModal(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSubmitNotice}
                            disabled={submitting || !newNotice.title.trim() || !newNotice.content.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {submitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Submit for Review
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default CommunityPage;
